// Background Animation Module
// Handles JS-driven background animations for animated sections
import { addAction, INIT, LAYOUTEND } from '@situation/setdesign.util';
import StarsAnimation from '../animations/StarsAnimation';
import SmokeAnimation from '../animations/SmokeAnimation';

// Store animation instances for lifecycle management
const animationInstances = new Map();

/**
 * Load required scripts dynamically using SetDesign.loadScript
 * This prevents loading heavy libraries (GSAP, Three.js) when animations are hidden by randomization
 * @param {HTMLElement} container - Animation container element with data-requires-scripts attribute
 * @returns {Promise} - Promise that resolves when all scripts are loaded
 */
const loadRequiredScripts = (container) => {
    const scriptsAttr = container.dataset.requiresScripts;
    if (!scriptsAttr) {
        return Promise.resolve();
    }

    const scriptNames = scriptsAttr.split(',').map((s) => s.trim());
    const scriptUrls = window.sit?.bgAnimations?.scripts || {};
    const promises = scriptNames.map((name) => {
        const url = scriptUrls[name];
        if (!url) {
            console.error(`Script URL not found for: ${name}`);
            return Promise.reject(new Error(`Missing script: ${name}`));
        }
        // SetDesign.loadScript returns a deferred promise and handles caching
        return window.SetDesign.loadScript(name, url);
    });
    return Promise.all(promises);
};

// Initialize all stars animations on ready
addAction(INIT, () => {
    // Find all star containers with data-stars-animation attribute
    document.querySelectorAll('[data-stars-animation]').forEach((container) => {
        // Skip if inside randomize-block but not the selected one
        // (randomize-block.js runs before INIT, so randomization is already done)
        const randomizeBlock = container.closest('.randomize-block');
        if (randomizeBlock) {
            // Check if any ancestor up to randomize-block has randomize-visible
            const visibleBlock = container.closest('.randomize-visible');
            if (!visibleBlock) {
                return;
            }
        }

        const canvas = container.querySelector('canvas');
        if (!canvas) return;

        // Get panel for sizing (or use container itself)
        const panel = container.querySelector('.animation-panel') || container;
        let animation = null;
        let isInViewport = false;
        let isTabVisible = !document.hidden;

        /**
         * Sync animation state based on visibility
         * Only run animation when BOTH in viewport AND tab is visible
         */
        const updateAnimation = () => {
            const shouldRun = isInViewport && isTabVisible;
            if (shouldRun && !animation) {
                // Load required scripts first, then initialize animation
                loadRequiredScripts(container)
                    .then(() => {
                        // Double-check we still should run (visibility may have changed during load)
                        if (isInViewport && isTabVisible) {
                            animation = new StarsAnimation(canvas, panel, 1200);
                            animation.init();
                        }
                    })
                    .catch((error) => {
                        console.error('Failed to load stars animation dependencies:', error);
                    });
            } else if (!shouldRun && animation) {
                // Destroy animation (frees memory, stops spawner)
                animation.destroy();
                animation = null;
            }
        };

        // Use IntersectionObserver for viewport detection (~25x more efficient than scroll events)
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    isInViewport = entry.isIntersecting;
                    updateAnimation();
                });
            },
            {
                threshold: 0, // Fire when any pixel is visible
                rootMargin: '100px',
            },
        );
        observer.observe(canvas);

        // Tab visibility detection (pause when tab is hidden)
        const visibilityHandler = () => {
            isTabVisible = !document.hidden;
            updateAnimation();
        };

        document.addEventListener('visibilitychange', visibilityHandler);
        // Store instance with cleanup function
        animationInstances.set(canvas, {
            animation,
            observer,
            visibilityHandler,
            cleanup: () => {
                observer.disconnect();
                document.removeEventListener('visibilitychange', visibilityHandler);

                if (animation) {
                    animation.destroy();
                }
            },
        });
    });

    // Find all smoke containers with data-smoke-animation attribute
    document.querySelectorAll('[data-smoke-animation]').forEach((container) => {
        // Skip if inside randomize-block but not the selected one
        // (randomize-block.js runs before INIT, so randomization is already done)
        const randomizeBlock = container.closest('.randomize-block');
        if (randomizeBlock) {
            // Check if any ancestor up to randomize-block has randomize-visible
            const visibleBlock = container.closest('.randomize-visible');
            if (!visibleBlock) {
                return;
            }
        }
        // Skip initialization if container is hidden (display:none)
        if (container.offsetParent === null) {
            return;
        }

        const canvas = container.querySelector('canvas');
        if (!canvas) return;

        // Get panel for sizing (or use container itself)
        const panel = container.querySelector('.animation-panel') || container;
        let animation = null;
        let isInViewport = false;
        let isTabVisible = !document.hidden;

        /**
         * Sync animation state based on visibility
         * Only run animation when BOTH in viewport AND tab is visible
         */
        const updateAnimation = () => {
            const shouldRun = isInViewport && isTabVisible;
            if (shouldRun && !animation) {
                // Load required scripts first, then initialize animation
                loadRequiredScripts(container)
                    .then(() => {
                        // Double-check we still should run (visibility may have changed during load)
                        if (isInViewport && isTabVisible) {
                            animation = new SmokeAnimation(canvas, panel, 1200);
                            animation.init();
                        }
                    })
                    .catch((error) => {
                        console.error('Failed to load smoke animation dependencies:', error);
                    });
            } else if (!shouldRun && animation) {
                // Destroy animation (frees memory, stops animation)
                animation.destroy();
                animation = null;
            }
        };

        // Use IntersectionObserver for viewport detection (~25x more efficient than scroll events)
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    isInViewport = entry.isIntersecting;
                    updateAnimation();
                });
            },
            {
                threshold: 0, // Fire when any pixel is visible
                rootMargin: '100px',
            },
        );
        observer.observe(canvas);

        // Tab visibility detection (pause when tab is hidden)
        const visibilityHandler = () => {
            isTabVisible = !document.hidden;
            updateAnimation();
        };

        document.addEventListener('visibilitychange', visibilityHandler);
        // Store instance with cleanup function
        animationInstances.set(canvas, {
            animation,
            observer,
            visibilityHandler,
            cleanup: () => {
                observer.disconnect();
                document.removeEventListener('visibilitychange', visibilityHandler);

                if (animation) {
                    animation.destroy();
                }
            },
        });
    });
});

// Handle resize events (debounced via LAYOUTEND)
addAction(LAYOUTEND, () => {
    animationInstances.forEach((instance) => {
        // Only resize if animation is currently running
        if (instance.animation && instance.animation.handleResize) {
            instance.animation.handleResize();
        }
    });
});

// Export cleanup function for manual teardown (e.g., AJAX navigation)
export default function cleanupAllAnimations() {
    animationInstances.forEach((instance) => {
        instance.cleanup();
    });

    animationInstances.clear();
}
